import { useCallback } from "react";
import { SelfDrivingService } from "../../server/api/self-driving";
import { useToken } from "../../server/auth/use-token";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { authHeader } from "../../utils/auth-header";
import { SupabaseFontSelectionSummaryService } from "../brand-kit/server/font-selection-summary-service";
import { trackContentGenerationResult } from "../content-generation/track-content-generation";
import { brandInfoToCustomizationsNew } from "../design-huddle/brand-info-customizations/brand-info-customizations";
import { selfDriving } from "../self-driving/self-driving";

import type {
  MultiformatIcons,
  MultiformatPhotos,
  MultiformatVideos,
} from "../../server/api/self-driving";
import type { UUID } from "../../utils/uuid";
import type { FontSelectionSummary } from "../brand-kit/hooks/use-font-selections";
import type { BrandKitImageType, BrandKitStyle } from "../brand-kit/server/brand-kit-service";
import type { BrandInfo, BrandInfoSelected } from "../brand-kit/types";
import type { ImageUrl } from "../multiformat/store/preview-family";
import type {
  MultiformatImage,
  MultiformatImageType,
  MultiformatLayout,
} from "../multiformat/store/types";
import type { CommsImageLoaderState } from "./generate-comms-context";

export const useGetCommsImages = (
  format: string,
  description: string,
  industry?: string,
  fileId?: string,
  db?: string,
  layouts?: ({ layout: MultiformatLayout; index: number } | undefined)[],
  contentGenerationId?: UUID | null,
): ((config: {
  branding: BrandInfoSelected;
  brandingImageType: "square" | "banner";
  brandingBar: Pick<BrandInfoSelected, "color" | "logo">;
  brandInfo: BrandInfo | null;
  onImageLoaded: (imgNum: number, state: CommsImageLoaderState) => void;
  signal: AbortSignal;
}) => Promise<void>) => {
  const getToken = useToken();
  const getFontSelection = useGetFontSelection();

  function imageTypeRequired(mfLayouts: MultiformatLayout[], type: MultiformatImageType): boolean {
    return mfLayouts?.some((layout) => layout.images.some((image) => image.type === type));
  }

  return useCallback(
    async ({ branding, brandingImageType, brandingBar, brandInfo, onImageLoaded, signal }) => {
      const throwIfAborted = (): void => {
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      };

      const filteredLayouts = layouts?.filter((l) => l !== undefined);

      // TODO: handle no layouts - error state
      if (!filteredLayouts) return;
      throwIfAborted();

      const service = new SelfDrivingService(await getToken());

      const photosRequired = imageTypeRequired(
        filteredLayouts.map((l) => l.layout),
        "photo",
      );
      const photosPromise = photosRequired
        ? service.photos(format, description, undefined, industry, fileId, db)
        : Promise.resolve(null);

      const iconsRequired = imageTypeRequired(
        filteredLayouts.map((l) => l.layout),
        "icon",
      );
      const iconsPromise = iconsRequired
        ? service.icons(format, description, fileId)
        : Promise.resolve(null);

      const videosRequired = imageTypeRequired(
        filteredLayouts.map((l) => l.layout),
        "video",
      );
      const videosPromise = videosRequired
        ? service.videos(format, description, fileId)
        : Promise.resolve(null);

      const fontSelectionPromise = getFontSelection();
      throwIfAborted();

      const token = await getToken();
      await Promise.all(
        filteredLayouts.map(async ({ layout: l, index: imgNum }) => {
          throwIfAborted();

          onImageLoaded(imgNum, { loading: true, data: null, error: null });
          const layoutPromise = selfDriving.POST("/multiformat/{formatId}/image/{imgNum}", {
            body: {
              description,
              fileId,
            },
            params: {
              path: { formatId: format, imgNum: (imgNum + 1).toString() },
            },
            ...authHeader(token),
            signal,
          });
          const [{ data: layout, error, response }, photos, icons, videos, fontSelections] =
            await Promise.all([
              layoutPromise,
              photosPromise,
              iconsPromise,
              videosPromise,
              fontSelectionPromise,
            ]);

          throwIfAborted();

          if (!response.ok || error || !l.images || !layout) {
            onImageLoaded(imgNum, { loading: true, data: null, error: null });
            return;
          }

          const customizations = brandInfoToCustomizationsNew({
            brandInfoSelected: brandingBar,
            useSelectedLogo: true,
            brandInfo,
            mergeTags: layout.response.merge_tags ?? [],
            imageUrls: getImageUrls(
              l.images,
              photos?.data ? photos.data?.photos : [],
              icons?.data ? icons.data.icons : [],
              videos?.data ? videos.data.videos : [],
              layout.response.url,
            ),
            fontSelections: fontSelections ?? [],
          });

          const templateCode = mapTemplateCode(
            branding?.branding_type,
            brandingImageType,
            format,
            l.design_huddle_id,
          );

          if (!templateCode) {
            if (!signal?.aborted) onImageLoaded(imgNum, { loading: true, data: null, error: null });
            return;
          }

          const pageNumber = imgNum + 1;
          const previewOptions = {
            formatId: format,
            title: layout.response.title,
            template_code: templateCode,
            customizations,
            page_number: pageNumber,
            width: 1200,
            height: brandingImageType === "square" ? 1200 : 400,
          };

          const url = await new Promise<string>((resolve, reject) => {
            DSHDLib.getVariableTemplatePreviewURL(previewOptions, (err, previewUrl) => {
              if (err) {
                reject(err);
              } else {
                resolve(previewUrl);
              }
            });
          });

          let contentGenerationResultId: UUID | null = null;
          if (contentGenerationId && layout) {
            contentGenerationResultId = await trackContentGenerationResult(
              token,
              contentGenerationId,
              layout,
            );
          }

          if (!signal.aborted) {
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            onImageLoaded(imgNum, {
              data: {
                url,
                templateCode,
                pageNumber,
                customizations,
                title: layout.response.title,
                subjectLine:
                  layout.response.merge_tags.find((m) => m.name === "subject-line")?.value ?? "",
                imageType: brandingImageType,
                contentGenerationResultId,
              },
              loading: false,
              error: null,
            } as CommsImageLoaderState);
          }
        }),
      );
    },
    [
      description,
      format,
      getFontSelection,
      getToken,
      industry,
      fileId,
      db,
      layouts,
      contentGenerationId,
    ],
  );
};

const useGetFontSelection = (): (() => Promise<FontSelectionSummary[] | undefined>) =>
  useSupabaseCallback(async ({ supabase, account_id }) => {
    const { data, error } = await new SupabaseFontSelectionSummaryService(supabase).getAll(
      account_id,
      { order: [{ column: "default_for", dir: "asc" }] },
    );

    if (error) throw error;

    return data;
  }, []);

interface VideoAsset {
  type: "video";
  asset_id: number;
  index: number;
  tag: string;
}
function getImageUrls(
  images: MultiformatImage[],
  photos: MultiformatPhotos["photos"],
  icons: MultiformatIcons["icons"],
  videos: MultiformatVideos["videos"],
  generatedImageUrl?: string,
): ImageUrl[] {
  const imageUrls: ImageUrl[] = images
    .map((image): ImageUrl | null => {
      const { type, index } = image;

      if (type === "photo") {
        if (photos) {
          const orientedPhotos = photos.filter((p) => p.orientation === image.orientation);
          const indexedUrl = orientedPhotos?.[index]?.url;
          const fallbackUrl =
            orientedPhotos.length > 0
              ? orientedPhotos[Math.floor(Math.random() * orientedPhotos.length)]!.url
              : undefined;
          return { ...image, type: "photo", url: indexedUrl || fallbackUrl || "" };
        }
      }

      if (type === "icon" && icons) {
        return { ...image, type: "icon", url: icons[index]?.url ?? "" };
      }

      if (type === "illustration" && generatedImageUrl) {
        return { ...image, type: "illustration", url: generatedImageUrl };
      }

      if (type === "video" && videos && videos[index]) {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        return {
          ...image,
          asset_id: videos[index].dh_asset_id,
        } as VideoAsset;
      }

      return { ...image, type: "photo", url: "" };
    })
    .filter((imageUrl): imageUrl is ImageUrl => imageUrl !== null);

  return imageUrls;
}

const bannerTemplateNames = {
  bold: "AI Bold Banner",
  sophisticated: "AI Sophisticated Banner",
  organic: "AI Organic Banner",
  minimalist: "AI Minimalist Banner",
  formal: "AI Formal Banner",
};

const squareTemplateNames = {
  bold: "Bold Square AI",
  sophisticated: "Sophisticated Square AI",
  organic: "Organic Square AI",
  minimalist: "Minimalist Square AI",
  formal: "Formal Square AI",
};

function mapTemplateCode(
  brandKitStyle: BrandKitStyle | undefined,
  imageType: BrandKitImageType,
  format: string,
  templateCode: string,
): string {
  if (brandKitStyle?.type === "custom") return brandKitStyle.value;

  if (format.toLowerCase() === "communication" && brandKitStyle) {
    return imageType === "square"
      ? squareTemplateNames[brandKitStyle.value]
      : bannerTemplateNames[brandKitStyle.value];
  }

  return templateCode;
}
